import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, Renderer2, RendererFactory2, Self, SimpleChanges, ViewChild} from '@angular/core';
import moment, {Moment} from 'moment-timezone';
import {PlacementArray} from '@ng-bootstrap/ng-bootstrap/util/positioning';
import {ControlValueAccessor, FormControl, FormGroup, NgControl, ValidationErrors} from '@angular/forms';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {DateSelectComponentFields, DateTimeRange, DateTimeRangeLabel} from '@shared/components/date-time-range-select/date-time-range-select.component';
import {Subject} from 'rxjs';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {DateTimeRangeService} from '@shared/components/date-time-range-picker/date-time-range.service';
import {ElementIdGenerationService} from '@shared/services';
import {ValidatorUtils} from '@shared/util/validators/validator-utils';
import {momentsEqual} from '@shared/util/comparitors/moment-comparator';
import {I18nMessage} from '@shared/api/model/i18n';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {ParamMap, Params} from '@angular/router';
import {updateQueryParam} from '@app/shared/util/angular/angular-utils';
import {AppConstants} from '@app/shared/util/app-constants';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {StringUtils} from '@app/shared/util/general/string-utils';
import {BrowserUtils} from '@app/shared/util/general/browser-utils';

enum FormControlName {
  DATE_RANGE = 'dateRange',
  RELATIVE_DATE = 'relativeDate',
  INCLUDE_TODAY = "includeToday",
}

export enum DateTimeRangeMode {
  ABSOLUTE = 'Absolute',
  RELATIVE = 'Relative'
}

interface RelativeDateRangeItem {
  label: I18nMessage;
  subText: I18nMessage;
  range: DateTimeRange;
  rangeType: RelativeDateRange;
}

interface PickerFormValue {
  [FormControlName.DATE_RANGE]?: DateTimeRange;
  [FormControlName.RELATIVE_DATE]?: RelativeDateRange;
  [FormControlName.INCLUDE_TODAY]?: boolean;
}

export interface RelativeDateTimeRange extends DateTimeRange {
  relativeDate?: RelativeDateRange;
  includeToday?: boolean;
}

type PickerForm = {
  [Property in keyof PickerFormValue]: FormControl<PickerFormValue[Property]>;
}

export enum RelativeDateRange {
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last_7_days',
  LAST_14_DAYS = 'last_14_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_90_DAYS = 'last_90_days',
  LAST_MONTH = 'last_month',
  LAST_3_MONTHS = 'last_3_months',
  LAST_QUARTER = 'last_quarter',
  LAST_6_MONTHS = 'last_6_months',
  LAST_12_MONTHS = 'last_12_months'
}

export const relativeDateRangeLabelMap: Record<RelativeDateRange, string> = {
  [RelativeDateRange.YESTERDAY]: 'Yesterday',
  [RelativeDateRange.LAST_7_DAYS]: 'Last 7 days',
  [RelativeDateRange.LAST_14_DAYS]: 'Last 14 days',
  [RelativeDateRange.LAST_30_DAYS]: 'Last 30 days',
  [RelativeDateRange.LAST_90_DAYS]: 'Last 90 days',
  [RelativeDateRange.LAST_MONTH]: 'Last month',
  [RelativeDateRange.LAST_3_MONTHS]: 'Last 3 months',
  [RelativeDateRange.LAST_QUARTER]: 'Last quarter',
  [RelativeDateRange.LAST_6_MONTHS]: 'Last 6 months',
  [RelativeDateRange.LAST_12_MONTHS]: 'Last 12 months'
};

enum DateFormat {
  MONTH = 'MMM',
  SHORT_DATE = 'D MMM',
  DATE = 'DD MMM YY',
  DATE_TIME = 'DD MMM YY HH:mm'
}

interface GroupedRelativeDateRanges {
  yesterday: RelativeDateRangeItem;
  days: RelativeDateRangeItem[];
  months: RelativeDateRangeItem[];
  quarter: RelativeDateRangeItem;
}

export interface RelativeDateParamNames {
  startFieldName: string;
  endFieldName: string;
  relativeFieldName: string;
  includeTodayFieldName: string;
}

@Component({
  selector: 'app-date-time-range-picker',
  templateUrl: './date-time-range-picker.component.html',
  styleUrls: ['./date-time-range-picker.component.scss'],
  standalone: false
})
export class DateTimeRangePickerComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  static readonly DEFAULT_LABEL: DateTimeRangeLabel = {
    [DateSelectComponentFields.START_DATE]: {messageKey: 'Start Date'},
    [DateSelectComponentFields.END_DATE]: {messageKey: 'End Date'}
  };

  private readonly DEFAULT_AUTO_CLOSE_VALUE: NgbDropdown['autoClose'] = 'outside';
  private readonly DROPDOWN_CLASS_NAME = 'dropdown-toggle';
  private readonly renderer: Renderer2;
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly logger: AceLogger;
  private readonly rangesToDisplayAsMonths = new Set<RelativeDateRange>([
    ...DateTimeRangePickerComponent.monthToSubtractByRelativeRange.keys(),
    RelativeDateRange.LAST_QUARTER
  ]);
  private readonly DEFAULT_RELATIVE_DATE_RANGES = Object.values(RelativeDateRange).filter(rangeType => rangeType !== RelativeDateRange.LAST_14_DAYS);

  @Input() label: DateTimeRangeLabel = DateTimeRangePickerComponent.DEFAULT_LABEL;
  @Input() minDate: Moment;
  @Input() maxDate: Moment;
  @Input() readOnly = false;
  @Input() requiredFields: DateSelectComponentFields[];
  @Input() showTime = false;
  @Input() placement: PlacementArray = ['bottom-start', 'bottom-end', 'top-start', 'top-end'];
  @Input() timezone: string;
  @Input() allowEqual = true;
  @Input() placeholder = '';
  @Input() showErrors = true;
  @Input() showClearButton: boolean;
  @Input() container: NgbDropdown['container'] = 'body';
  @Input() mode: DateTimeRangeMode = DateTimeRangeMode.ABSOLUTE;
  @Input() inverted: boolean = false;
  @Input() showIncludeToday: boolean = false;

  @Input()
  get relativeDateRanges(): RelativeDateRange[] {
    return this._relativeDateRanges;
  }
  // use the setter here to NOT rely on ngOnChanges for default when relativeDateRanges is undefined from caller
  // both angular component and angular form directive have ngOnChanges, and the order of execution is not guaranteed
  // if form directly ngOnChanges is called first, then in this component, this.writeValue is triggered before this.ngOnChanges
  // if the parent component passed in undefined to relativeDateRanges, then by the time this.writeValue is called,
  // this.relativeDateRanges remained undefined.
  // using setter made sure the default is always coerced at construction.
  set relativeDateRanges(value: RelativeDateRange[] | null) {
    this._relativeDateRanges = value ?? this.DEFAULT_RELATIVE_DATE_RANGES;
  }

  @Output() openChange = new EventEmitter<boolean>();

  @ViewChild('dropDown', {static: false}) dropDown: NgbDropdown;

  protected readonly pickerForm: FormGroup<PickerForm>;
  protected readonly formControlName = FormControlName;
  protected readonly dropdownId: string;
  protected readonly dropdownMenuClass: string;
  protected readonly dateTimeRangeMode = DateTimeRangeMode;
  protected readonly relativeDateRangeLabelMap = relativeDateRangeLabelMap;

  // Need to ensure that the dropdown is not closed when Custom Range option is selected. To achieve this need to set
  // autoClose value to 'outside' rather than true, and use code to close the dropdown when the user selects an option
  // other than Custom Range
  protected dropDownAutoClose: NgbDropdown['autoClose'] = this.DEFAULT_AUTO_CLOSE_VALUE;
  protected groupedRelativeDateRanges: GroupedRelativeDateRanges;

  private _relativeDateRanges: RelativeDateRange[] = this.DEFAULT_RELATIVE_DATE_RANGES;
  private showCustomRange = false;
  private currentValue: PickerFormValue;

  private effectiveTimezone = moment.tz.guess();

  private onChangeFn: (value: RelativeDateTimeRange) => void = (_: RelativeDateTimeRange) => {};
  private onTouchedFn: () => void = () => {};

  // Relative ranges whose end can be extended to include the current, in-progress period: day ranges
  // extend to today, month ranges to the current month. Driven by the single `includeToday` flag.
  private static readonly canIncludeToday = new Set([
    RelativeDateRange.LAST_7_DAYS,
    RelativeDateRange.LAST_14_DAYS,
    RelativeDateRange.LAST_30_DAYS,
    RelativeDateRange.LAST_90_DAYS,
    RelativeDateRange.LAST_MONTH,
    RelativeDateRange.LAST_3_MONTHS,
    RelativeDateRange.LAST_6_MONTHS,
    RelativeDateRange.LAST_12_MONTHS
  ]);
  private static readonly monthToSubtractByRelativeRange = new Map([
    [RelativeDateRange.LAST_12_MONTHS, 12],
    [RelativeDateRange.LAST_6_MONTHS, 6],
    [RelativeDateRange.LAST_3_MONTHS, 3],
    [RelativeDateRange.LAST_MONTH, 1]
  ]);
  private static readonly daysToSubtractByRelativeRange = new Map([
    [RelativeDateRange.YESTERDAY, 1],
    [RelativeDateRange.LAST_7_DAYS, 7],
    [RelativeDateRange.LAST_14_DAYS, 14],
    [RelativeDateRange.LAST_30_DAYS, 30],
    [RelativeDateRange.LAST_90_DAYS, 90],
  ]);

  constructor(
    private cdr: ChangeDetectorRef,
    private dateTimeRangeService: DateTimeRangeService,
    @Optional() @Self() protected controlDir: NgControl,
    logService: AceLoggerService,
    elementIdGenerationService: ElementIdGenerationService,
    rendererFactory: RendererFactory2
  ) {
    if (controlDir) {
      controlDir.valueAccessor = this;
    }
    this.renderer = rendererFactory.createRenderer(null, null);
    this.pickerForm = new FormGroup<PickerForm>({
      [FormControlName.DATE_RANGE]: new FormControl(null),
      [FormControlName.RELATIVE_DATE]: new FormControl(null),
      [FormControlName.INCLUDE_TODAY]: new FormControl(false)
    });
    const generator = elementIdGenerationService.buildIdGenerator();
    this.dropdownId = generator.getId('date-time-range-picker');
    // Use a dynamic class name to ensure there are no clashes between multiple components on the same page
    this.dropdownMenuClass = 'dropdown-menu-' + this.dropdownId;
    this.logger = logService.create(DateTimeRangePickerComponent.name);
  }

  ngOnInit() {
    if (this.requiredFields == null) {
      this.initRequiredFields();
    }

    if (this.showClearButton == null) {
      this.initShowClear();
    }

    if (!this.groupedRelativeDateRanges) {
      this.updateRelativeDateRangeItems();
    }

    this.setRelativeControlStatusByRangeMode();

    this.pickerForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
      if (this.isPickerInCustomRangeMode() && value?.[FormControlName.RELATIVE_DATE] != null) {
        this.pickerForm.get(FormControlName.RELATIVE_DATE).setValue(null);
      } else {
        const shouldEmit = this.shouldEmitValue(this.currentValue, value);
        this.currentValue = value;
        if (this.onChangeFn && shouldEmit) {
          this.onChangeFn(this.hasPropertyValues(value) && this.hasAllRequiredFields(value[FormControlName.DATE_RANGE])
            ? this.getValue(value)
            : null);
        }
      }
    });
    this.pickerForm.get(FormControlName.DATE_RANGE).statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.processControlErrors());
    this.pickerForm.get(FormControlName.INCLUDE_TODAY).valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.updateRelativeDateRangeItems();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showClearButton) {
      this.initShowClear();
    }

    if (changes.requiredFields) {
      this.initRequiredFields();
    }

    if (changes.mode) {
      if (changes.mode.currentValue == null) {
        this.mode = DateTimeRangeMode.ABSOLUTE;
      }
      this.setRelativeControlStatusByRangeMode();
    }

    let shouldUpdateRangeItems = false;
    if (changes.timezone) {
      this.effectiveTimezone = this.timezone ?? moment.tz.guess();
      shouldUpdateRangeItems = true;
    }
    if (changes.readOnly) {
      this.setDisabledState(this.readOnly);
    }
    if (changes.minDate || changes.maxDate || changes.allowEqual || changes.relativeDateRanges) {
      shouldUpdateRangeItems = true;
    }

    if (shouldUpdateRangeItems) {
      this.updateRelativeDateRangeItems();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onTouched(): void {
    this.pickerForm.get(FormControlName.DATE_RANGE).markAsTouched();
    if (this.onTouchedFn) {
      this.onTouchedFn();
    }
  }

  onOpenChange(isOpen: boolean) {
    if (isOpen) {
      this.updateDropdownMinWidth();
    } else {
      this.setShowCustomRange(false);
    }
    this.openChange.emit(isOpen);
  }

  setDisabledState(disabled: boolean) {
    disabled ? this.pickerForm.disable({emitEvent: false}) : this.pickerForm.enable({emitEvent: false});
  }

  closePicker() {
    this.dropDown.close();
  }

  setShowCustomRange(event: boolean) {
    this.showCustomRange = event;
    if (this.showCustomRange) {
      // This call is needed in case the control has errors when the custom date range picker is shown.
      // For some reason calling cdr.detectChanges() only from within processControlErrors() does not work, and still
      // results in ExpressionChangedAfterItHasBeenCheckedError due to the 'ng-valid' field changing from 'true' to
      // 'false'. The ExpressionChangedAfterItHasBeenCheckedError does not describe which control has the issue, so it
      // is difficult to narrow down
      this.cdr.detectChanges();
    }
  }

  onSelect(event: RelativeDateRange): void {
    const dateRange = this.getDatesByRelativeDateRange(event, this.pickerForm.get(FormControlName.INCLUDE_TODAY).value);
    this.pickerForm.patchValue({
      [FormControlName.DATE_RANGE]: dateRange,
      [FormControlName.RELATIVE_DATE]: event
    });
    // As the autoCLose is not true, need to close the dropdown
    this.dropDown.close();
  }

  setIncludeToday(includeToday: boolean) {
    const dateRange = DateTimeRangePickerComponent.resolveValueAsRange(
      {...this.getValue(this.pickerForm.value), includeToday},
      this.effectiveTimezone,
      this.showTime
    );
    const partialUpdatedFormValue: Partial<PickerFormValue> = {
      [FormControlName.INCLUDE_TODAY]: includeToday,
      [FormControlName.DATE_RANGE]: dateRange
    };
    this.pickerForm.patchValue(partialUpdatedFormValue);
  }

  onCalendarPickerOpen(): void {
    this.dropDownAutoClose = false;
  }

  onCalendarPickerClosed(): void {
    this.dropDownAutoClose = this.DEFAULT_AUTO_CLOSE_VALUE;
  }

  getPickerLabel(relativeDateRange: DateTimeRange, dateFormat: DateFormat, forceSingleDate: boolean = false): string | null {
    if (relativeDateRange?.[DateSelectComponentFields.START_DATE] || relativeDateRange?.[DateSelectComponentFields.END_DATE]) {
      const {startDate, endDate} = relativeDateRange;
      if (momentsEqual(startDate, endDate) || forceSingleDate) {
        return this.formatTimestamp(startDate, dateFormat);
      }
      return this.formatTimestamp(startDate, dateFormat) + ' - ' + this.formatTimestamp(endDate, dateFormat);
    } else {
      return null;
    }
  }

  isPickerInCustomRangeMode(): boolean {
    return !this.groupedRelativeDateRanges || this.showCustomRange;
  }

  getRelativeDateRangeTooltipMessage(formValue: PickerFormValue): I18nMessage {
    return {
      messageKey: 'Single Param',
      parameters: {
        param: this.getPickerLabel(
          this.getValue(formValue),
          this.showTime
            ? DateFormat.DATE_TIME
            : DateFormat.DATE
        )
      }
    };
  }

  getDisplayMessage(relativeDate: RelativeDateRange | null, dateTimeRange: DateTimeRange | null): I18nMessage | null {
    if (relativeDate) {
      const dateRangeMessage = {messageKey: this.relativeDateRangeLabelMap[relativeDate]};
      return this.inverted
        ? {
          messageKey: 'not during dateRange',
          parameters: {
            dateRange: dateRangeMessage
          }
        }
        : dateRangeMessage;
    } else if (dateTimeRange) {
      const startDate = dateTimeRange[DateSelectComponentFields.START_DATE];
      const endDate = dateTimeRange[DateSelectComponentFields.END_DATE];
      const tsFormat = this.showTime ? DateFormat.DATE_TIME : DateFormat.DATE;
      if (startDate && endDate) {
        const dateRangeText = this.getPickerLabel(dateTimeRange, tsFormat);
        return this.inverted
          ? {
            messageKey: 'not during dateRange',
            parameters: {
              dateRange: dateRangeText
            }
          }
          : {messageKey: "Single Param", parameters: {param: dateRangeText}};
      } else if (startDate) {
        return {
          messageKey: this.inverted ? 'before date' : 'after date',
          parameters: {
            date: this.formatTimestamp(startDate, tsFormat)
          }
        };
      } else if (endDate) {
        return {
          messageKey: this.inverted ? 'after date' : 'before date',
          parameters: {
            date: this.formatTimestamp(endDate, tsFormat)
          }
        };
      }
    }
    this.logger.error('Expected `relativeDate` or `dateTimeRange` but both were empty');
    return null;
  }

  public static canRelativeRangeIncludeToday(relativeDate: RelativeDateRange | null): boolean {
    return relativeDate != null && this.canIncludeToday.has(relativeDate);
  }

  public static cloneRelativeDateTimeRange(relativeDateRange: RelativeDateTimeRange | null): RelativeDateTimeRange | null {
    if (relativeDateRange) {
      const startDate = relativeDateRange[DateSelectComponentFields.START_DATE]?.clone() ?? null;
      const endDate = relativeDateRange[DateSelectComponentFields.END_DATE]?.clone() ?? null;
      return {
        [DateSelectComponentFields.START_DATE]: startDate,
        [DateSelectComponentFields.END_DATE]: endDate,
        ...relativeDateRange.relativeDate && {relativeDate: relativeDateRange.relativeDate},
        ...relativeDateRange.includeToday != null && {includeToday: relativeDateRange.includeToday}
      };
    } else {
      return relativeDateRange;
    }
  }

  public static getDatesByRelativeDateRange(rangeType: RelativeDateRange, effectiveTimezone: string, showTime: boolean, includeToday: boolean = false): DateTimeRange {
    const monthRange = this.monthToSubtractByRelativeRange.get(rangeType);
    const startOfDate = moment.tz(effectiveTimezone);
    if (!showTime) {
      startOfDate.startOf('day');
    }
    if (monthRange != null) {
      const startOfMonth = startOfDate.clone().startOf('month');
      const startDate = startOfMonth.clone().subtract(monthRange, 'months');
      let endDate: Moment;
      if (includeToday) {
        // include the current month only up to today, so the range never runs into the future
        endDate = startOfDate.clone();
      } else {
        endDate = startOfMonth.clone();
        if (!showTime) {
          endDate.subtract(1, 'day');
        }
      }
      return {startDate, endDate};
    } else if (rangeType === RelativeDateRange.LAST_QUARTER) {
      const startOfQuarter = startOfDate.clone().startOf('quarter');
      const startDate = startOfQuarter.clone().subtract(1, 'quarter');
      const endDate = showTime ? startOfQuarter.clone() : startOfQuarter.clone().subtract(1, 'day');
      return {startDate, endDate};
    }

    let startDate: Moment;
    const daysToSubtract = this.daysToSubtractByRelativeRange.get(rangeType);
    if (daysToSubtract != null) {
      startDate = startOfDate.clone().subtract(daysToSubtract, 'days');
    } else {
      throw new Error('Invalid RelativeDateRange has been selected ' + rangeType);
    }
    const endDate = startOfDate.clone();
    if (!showTime && (!includeToday || !this.canRelativeRangeIncludeToday(rangeType))) {
      endDate.subtract(1, 'days');
    }
    return {startDate, endDate};
  }

  /** Returns a start and end date whether `siteAndDateRange` is a relative or an absolute date range
   * @param adjustTimeForTimezone - only matters if siteAndDateRange has absolute (i.e. not relative) dates, as relative dates are always resolved using the provided timezone.
   *   When the value has absolute dates, `true` will shift the time when setting the provided timezone on the dates (i.e. the timezone will be converted, rather than
   *   'forced'). false, will force the timezone (i.e. set it on the dates without time shifting). */
  public static resolveValueAsRange(siteAndDateRange: RelativeDateTimeRange | null, timezone: string, showTime: boolean, adjustTimeForTimezone?: boolean): DateTimeRange | null {
    if (siteAndDateRange?.relativeDate) {
      return this.getDatesByRelativeDateRange(siteAndDateRange.relativeDate, timezone, showTime, siteAndDateRange.includeToday);
    }
    const startDate = siteAndDateRange?.[DateSelectComponentFields.START_DATE]?.clone().tz(timezone, !adjustTimeForTimezone) ?? null;
    const endDate = siteAndDateRange?.[DateSelectComponentFields.END_DATE]?.clone().tz(timezone, !adjustTimeForTimezone) ?? null;
    return startDate || endDate
      ? {startDate, endDate}
      : null;
  }

  public static relativeDateTimeRangeEqual(range1: RelativeDateTimeRange | null, range2: RelativeDateTimeRange | null): boolean {
    return (range1 ?? null) === (range2 ?? null)
      || (range1 != null && range2 != null
        && (range1?.relativeDate != null || range2?.relativeDate != null
          ? (range1.relativeDate === range2.relativeDate
            && (range1.includeToday ?? false) === (range2.includeToday ?? false))
          : (momentsEqual(range1.startDate, range2.startDate)
            && momentsEqual(range1.endDate, range2.endDate))));
  }

  public static updateQueryParamsForDateTimeRange(params: Params,
                                                  relativeDateParamNames: RelativeDateParamNames,
                                                  relativeDateTimeRange: RelativeDateTimeRange | null): void {
    delete params[relativeDateParamNames.startFieldName];
    delete params[relativeDateParamNames.endFieldName];
    delete params[relativeDateParamNames.includeTodayFieldName];
    delete params[relativeDateParamNames.relativeFieldName];
    if (relativeDateTimeRange != null) {
      if (relativeDateTimeRange.relativeDate) {
        updateQueryParam(relativeDateTimeRange.relativeDate, relativeDateParamNames.relativeFieldName, params);
      } else if (relativeDateTimeRange.startDate || relativeDateTimeRange.endDate) {
        updateQueryParam(relativeDateTimeRange?.startDate?.format(AppConstants.TIMESTAMP_FORMAT), relativeDateParamNames.startFieldName, params);
        updateQueryParam(relativeDateTimeRange?.endDate?.format(AppConstants.TIMESTAMP_FORMAT), relativeDateParamNames.endFieldName, params);
      }
      /** When default `{includeToday: true}` is set on date range controls, the next time the query parameters needs to be updated with FormControl values,
       * the date range query will get `*includeToday=true` with no dates which we don't want happening. This checks if there are date values too aside from
       * just includeToday before setting query parameters for includeToday */
      if ((relativeDateTimeRange.relativeDate || relativeDateTimeRange.startDate || relativeDateTimeRange.endDate) && relativeDateTimeRange.includeToday != null) {
        updateQueryParam(relativeDateTimeRange.includeToday.toString(), relativeDateParamNames.includeTodayFieldName, params);
      }
    }
  }

  public static getDateTimeRangeFromParams(paramMap: ParamMap,
                                           relativeDateParamNames: RelativeDateParamNames,
                                           timezone?: string): RelativeDateTimeRange | null {
    const startParamValue: string | null = paramMap.get(relativeDateParamNames.startFieldName) ?? null;
    const endParamValue: string | null = paramMap.get(relativeDateParamNames.endFieldName) ?? null;
    const relativeParamValue: string | null = paramMap.get(relativeDateParamNames.relativeFieldName) ?? null;
    const relativeDate: RelativeDateRange | null = relativeParamValue
      ? ObjectUtils.getEnumValueFromString(RelativeDateRange, relativeParamValue)
      : null;
    const updatedAtIncludeTodayParamValue = paramMap.get(relativeDateParamNames.includeTodayFieldName);
    const updatedAtIncludeTodayValue: boolean | null = updatedAtIncludeTodayParamValue
      ? StringUtils.parseBoolean(updatedAtIncludeTodayParamValue)
      : null;

    if (relativeDate) {
      return {startDate: null, endDate: null, relativeDate, includeToday: updatedAtIncludeTodayValue};
    } else if (startParamValue || endParamValue) {
      const effectiveTimezone = timezone ?? BrowserUtils.getBrowserTimezone();
      return {
        startDate: startParamValue ? moment.tz(startParamValue, AppConstants.TIMESTAMP_FORMAT, effectiveTimezone) : null,
        endDate: endParamValue ? moment.tz(endParamValue, AppConstants.TIMESTAMP_FORMAT, effectiveTimezone) : null
      };
    }
    return null;
  }

  private initRequiredFields(): void {
    this.requiredFields = this.requiredFields ?? (ValidatorUtils.isRequiredControl(this.controlDir)
      ? [DateSelectComponentFields.START_DATE, DateSelectComponentFields.END_DATE] : []);
  }

  private initShowClear(): void {
    this.showClearButton = this.showClearButton ?? !ValidatorUtils.isRequiredControl(this.controlDir);
  }

  private setRelativeControlStatusByRangeMode(): void {
    if (this.pickerForm.enabled) {
      if (this.mode === DateTimeRangeMode.ABSOLUTE) {
        this.pickerForm.get(FormControlName.RELATIVE_DATE).disable();
      } else {
        this.pickerForm.get(FormControlName.RELATIVE_DATE).enable();
      }
    }
  }

  private buildDateRangeItemByRelativeDate(minDate: Moment,
                                           maxDate: Moment,
                                           relativeDateRanges: RelativeDateRange[],
                                           includeToday: boolean,
                                           allowEqual: boolean,
                                           showTime: boolean): Map<RelativeDateRange, RelativeDateRangeItem> {
    return relativeDateRanges.reduce((dateRangeItemByRelativeDate, rangeType) => {
      const relativeDates = this.getDatesByRelativeDateRange(rangeType, includeToday);
      const isStartDateValid = !minDate || !relativeDates.startDate.isBefore(minDate);
      const isEndDateValid = !maxDate || !relativeDates.endDate.isAfter(maxDate);
      const validEquals = allowEqual || relativeDates[DateSelectComponentFields.START_DATE].isBefore(relativeDates[DateSelectComponentFields.END_DATE]);
      if (isStartDateValid && isEndDateValid && validEquals) {
        const dateFormat = this.rangesToDisplayAsMonths.has(rangeType)
          ? DateFormat.MONTH
          : showTime
            ? DateFormat.DATE_TIME
            : DateFormat.SHORT_DATE;
        dateRangeItemByRelativeDate.set(rangeType, {
          label: {messageKey: relativeDateRangeLabelMap[rangeType]},
          // `Last month` is a single month and shown as one label, unless the current month is included, which makes it span two months
          subText: {messageKey: this.getPickerLabel(relativeDates, dateFormat, rangeType === RelativeDateRange.LAST_MONTH && !includeToday)},
          rangeType,
          range: relativeDates,
        });
      }
      return dateRangeItemByRelativeDate;
    }, new Map<RelativeDateRange, RelativeDateRangeItem>());
  }

  private updateRelativeDateRangeItems() {
    const includeToday = this.pickerForm.get(FormControlName.INCLUDE_TODAY).value;
    const dateRangeItemByRelativeDate = this.buildDateRangeItemByRelativeDate(
      this.minDate,
      this.maxDate,
      this.relativeDateRanges,
      includeToday,
      this.allowEqual,
      this.showTime);
    const yesterday = dateRangeItemByRelativeDate.get(RelativeDateRange.YESTERDAY);
    const days = [
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_7_DAYS),
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_14_DAYS),
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_30_DAYS),
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_90_DAYS)
    ].filter(d => d);
    const months = [
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_MONTH),
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_3_MONTHS),
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_6_MONTHS),
      dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_12_MONTHS),
    ].filter(m => m);
    const quarter = dateRangeItemByRelativeDate.get(RelativeDateRange.LAST_QUARTER);
    this.groupedRelativeDateRanges = {
      ...yesterday && {yesterday},
      ...days.length && {days},
      ...months.length && {months},
      ...quarter && {quarter}
    };
  }

  private getDatesByRelativeDateRange(rangeType: RelativeDateRange, includeToday: boolean = false): DateTimeRange {
    return DateTimeRangePickerComponent.getDatesByRelativeDateRange(rangeType, this.effectiveTimezone, this.showTime, includeToday);
  }

  private formatTimestamp(ts: Moment, dateFormat: DateFormat): string {
    return this.dateTimeRangeService.formatMoment(
      ts,
      this.effectiveTimezone,
      dateFormat,
    );
  }

  clearDateRange(event?: MouseEvent): void {
    event?.stopPropagation();
    this.onTouched();
    this.pickerForm.patchValue({[FormControlName.DATE_RANGE]: null, [FormControlName.RELATIVE_DATE]: null});
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  writeValue(value: RelativeDateTimeRange): void {
    const currentIncludeToday = this.pickerForm.get(FormControlName.INCLUDE_TODAY).value ?? null;
    const includeTodayChanged = (value?.includeToday ?? null) !== currentIncludeToday;
    let isIncludeTodayUpdated = false;
    if (this.mode === DateTimeRangeMode.RELATIVE && value?.relativeDate) {
      isIncludeTodayUpdated = this.writeValueInRelativeMode(value);
    } else {
      if (this.mode !== DateTimeRangeMode.RELATIVE && value?.relativeDate != null) {
        this.logger.error('In absolute mode the relative value must be null, but is,', value.relativeDate);
      }
      isIncludeTodayUpdated = this.writeValueInAbsoluteMode(value);
    }
    if (includeTodayChanged) {
      if (!isIncludeTodayUpdated) {
        /** For the case of `includeToday: null` being the only change, use `resolveIncludeToday` so that current range is checked if the change is applicable.
         * For example, when used with site control, and the last change is just a change in site id, and the date range value is `{relativeDate: 'Last month'}`
         * but the internal value is `{relativeDate: 'Last month', includeToday: true}`, using `value.includeToday` straight away would set `includeToday` to `false`
         * when its not the intended change. checking `canIncludeToday` first would prevent it which `resolveIncludeToday` does. */
        const includeToday = this.resolveIncludeToday(value, this.pickerForm.get(FormControlName.INCLUDE_TODAY).value ?? null);
        this.pickerForm.get(FormControlName.INCLUDE_TODAY)?.setValue(includeToday ?? false, {emitEvent: false});
      }
      this.updateRelativeDateRangeItems();
    }
  }

  private hasAllRequiredFields(value?: DateTimeRange): boolean {
    return this.requiredFields.every(fieldName => value?.[fieldName] != null);
  }

  private processControlErrors(): void {
    const dateRangeControl = this.pickerForm.get(FormControlName.DATE_RANGE);
    const dateControlErrors: ValidationErrors = {
      ...dateRangeControl.errors?.[DateSelectComponentFields.START_DATE],
      ...dateRangeControl.errors?.[DateSelectComponentFields.END_DATE],
    };
    const control = this.controlDir?.control;

    if (control) {
      const errors = {...control.errors};

      let updateDateRangeErrors = false;
      let updateErrors = false;

      if (this.hasPropertyValues(dateControlErrors)) {
        Object.assign(errors, dateControlErrors);
        updateErrors = true;
      }

      if (errors.required && this.requiredFields?.length) {
        dateControlErrors.required = true;
        updateDateRangeErrors = true;
      } else if (dateControlErrors.required) {
        delete dateControlErrors.required;
        updateDateRangeErrors = true;
      }

      if (updateDateRangeErrors) {
        dateRangeControl.setErrors(ValidatorUtils.normaliseErrors(dateControlErrors), {emitEvent: false});
      }

      if (updateErrors) {
        control.setErrors(ValidatorUtils.normaliseErrors(errors));
        this.cdr.detectChanges();
      }
    }
  }

  private updateDropdownMinWidth(): void {
    const dropdownElements = document.getElementsByClassName(this.DROPDOWN_CLASS_NAME);
    if (dropdownElements?.length) {
      const dropdownMenuElements = document.getElementsByClassName(this.dropdownMenuClass);
      if (dropdownMenuElements?.length) {
        const dropdownElement = dropdownElements.item(0);
        const dropdownMenuElement = dropdownMenuElements.item(0);
        const width = dropdownElement.getBoundingClientRect().width;
        this.renderer.setStyle(dropdownMenuElement, 'min-width', width + 'px');
      }
    }
  }

  /** Returns true if it updates the form */
  private writeValueInRelativeMode(value: RelativeDateTimeRange): boolean {
    if (value?.relativeDate !== this.pickerForm.get(FormControlName.RELATIVE_DATE).value) {
      const includeToday = this.resolveIncludeToday(value, this.pickerForm.get(FormControlName.INCLUDE_TODAY).value ?? null);
      const dateRange = this.getDatesByRelativeDateRange(value?.relativeDate, includeToday);
      this.setValueFromOutside({
        [FormControlName.DATE_RANGE]: dateRange,
        [FormControlName.RELATIVE_DATE]: value?.relativeDate,
        [FormControlName.INCLUDE_TODAY]: includeToday
      });

      if (value?.[DateSelectComponentFields.START_DATE] && !momentsEqual(dateRange?.startDate, value?.[DateSelectComponentFields.START_DATE])) {
        this.logger.warn('The provided start date', value?.[DateSelectComponentFields.START_DATE]?.format(), 'for relative date value',
          value?.relativeDate, 'is not equal to expected value', dateRange?.startDate?.format());
      }
      if (value?.[DateSelectComponentFields.END_DATE] && !momentsEqual(dateRange?.endDate, value?.[DateSelectComponentFields.END_DATE])) {
        this.logger.warn('The provided end date', value[DateSelectComponentFields.END_DATE].format(), 'for relative date value',
          value?.relativeDate, 'is not equal to expected value', dateRange?.endDate?.format());
      }
      return true;
    } else {
      return false
    }
  }

  /** Returns true if it updates the form */
  private writeValueInAbsoluteMode(value: RelativeDateTimeRange): boolean {
    const dateRange: DateTimeRange = value ? {
      [DateSelectComponentFields.START_DATE]: value.startDate ?? null,
      [DateSelectComponentFields.END_DATE]: value.endDate ?? null
    } : null;

    if (!this.dateRangeEqual(this.pickerForm.get(FormControlName.DATE_RANGE).value, dateRange)) {
      const includeToday = this.resolveIncludeToday(value, this.pickerForm.get(FormControlName.INCLUDE_TODAY).value ?? null);
      this.setValueFromOutside({
        [FormControlName.DATE_RANGE]: dateRange,
        [FormControlName.RELATIVE_DATE]: null,
        [FormControlName.INCLUDE_TODAY]: includeToday
      });
      return true;
    } else {
      return false;
    }
  }

  private getValue(value: PickerFormValue): RelativeDateTimeRange {
    if (this.mode === DateTimeRangeMode.RELATIVE) {
      const shouldHaveIncludeTodayProperty = value?.[FormControlName.INCLUDE_TODAY]
        && DateTimeRangePickerComponent.canRelativeRangeIncludeToday(value?.[FormControlName.RELATIVE_DATE] ?? null);
      return {
        ...value?.[FormControlName.DATE_RANGE],
        ...value?.[FormControlName.RELATIVE_DATE] && {[FormControlName.RELATIVE_DATE]: value?.[FormControlName.RELATIVE_DATE]},
        ...shouldHaveIncludeTodayProperty && {[FormControlName.INCLUDE_TODAY]: value?.[FormControlName.INCLUDE_TODAY]}
      };
    } else {
      return value[FormControlName.DATE_RANGE];
    }
  }

  private setValueFromOutside(value: PickerFormValue): void {
    this.pickerForm.setValue(value, {emitEvent: false});
    this.currentValue = value;
  }

  private dateRangeEqual(dateRange1: DateTimeRange | null, dateRange2: DateTimeRange | null): boolean {
    const actualDateRange1 = this.hasPropertyValues(dateRange1) ? dateRange1 : null;
    const actualDateRange2 = this.hasPropertyValues(dateRange2) ? dateRange2 : null;
    return (actualDateRange1 === actualDateRange2)
      || ((actualDateRange1 != null) && (actualDateRange2 != null)
        && momentsEqual(actualDateRange1[DateSelectComponentFields.START_DATE], actualDateRange2[DateSelectComponentFields.START_DATE])
        && momentsEqual(actualDateRange1[DateSelectComponentFields.END_DATE], actualDateRange2[DateSelectComponentFields.END_DATE]));
  }

  private shouldEmitValue(oldValue: PickerFormValue, value: PickerFormValue): boolean {
    return !this.dateRangeEqual(oldValue?.[this.formControlName.DATE_RANGE] ?? null, value?.[this.formControlName.DATE_RANGE] ?? null)
      || (oldValue?.[FormControlName.RELATIVE_DATE] ?? null) !== (value?.[FormControlName.RELATIVE_DATE] ?? null)
      || ((oldValue?.[FormControlName.INCLUDE_TODAY] ?? false) !== (value?.[FormControlName.INCLUDE_TODAY] ?? false)
        && DateTimeRangePickerComponent.canRelativeRangeIncludeToday(value?.[FormControlName.RELATIVE_DATE] ?? null));
  }

  /** If value has `includeToday`, use it. If it is `null`, check if `includeToday` is applicable to the `value`. If it is, then `includeToday`
   * is meant to be changed. If `includeToday` is not applicable, then it is not meant to change and should use existing `includeToday`. */
  private resolveIncludeToday(value: RelativeDateTimeRange, currentIncludeToday: boolean | null): boolean | null {
    return value?.includeToday
      ?? (DateTimeRangePickerComponent.canRelativeRangeIncludeToday(value?.relativeDate ?? null)
        ? false
        : currentIncludeToday);
  }

  private hasPropertyValues(value: object): boolean {
    return value
      ? Object.values(value).some(property => property != null)
      : false;
  }
}
